import * as React from "react";
import { Component } from "react";
import classnames from "classnames";
import { subHours } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";

import {
    OutageSeverity,
    StatusPageClient,
} from "@formant/core/client/StatusPageClient";
import { logger } from "@formant/core/common/logger";
import { Icon } from "@formant/ui/component/common/Icon";

import {
    statusPageDeploymentMetricId,
    statuspageEndpoint,
} from "../config/index";
import { Accordion } from "./common/Accordion";
import { FlexSpacer } from "./common/FlexSpacer";
import * as styles from "./OutageBanner.scss";

@observer
export class OutageBanner extends Component {
    @observable
    private severity: OutageSeverity = "none";

    @observable
    private message: string = "";

    private statusPageClient = new StatusPageClient(
        statuspageEndpoint,
        statusPageDeploymentMetricId,
    );

    private reportOutageState(severity: OutageSeverity, message: string) {
        this.severity = severity;
        this.message = message;
    }

    private closeBanner() {
        this.reportOutageState("none", "");
    }

    private updateBannerStatus(status: string) {
        switch (status) {
            case "none":
                this.reportOutageState("none", "");
                return;
            case "maintenance":
                this.reportOutageState(
                    "maintenance",
                    "Formant is undergoing scheduled maintainance",
                );
                return;
            case "deploying":
                this.reportOutageState(
                    "deploying",
                    "A new version of Formant is being deployed. Teleop sessions may be interrupted.",
                );
                return;
            case "critical":
                this.reportOutageState(
                    "critical",
                    "Formant is experiencing a critical outage.",
                );
                return;
            case "major":
                this.reportOutageState(
                    "major",
                    "Formant is experiencing a major outage.",
                );
                return;
            case "minor":
                this.reportOutageState(
                    "minor",
                    "Formant is experiencing a minor outage.",
                );
                return;
            default:
                logger.warn(`Received unsupported outage status: ${status}`);
                this.reportOutageState(
                    "minor",
                    "We're making some updates right now. Formant might run a little slower than usual.",
                );
        }
    }

    async componentDidMount() {
        const severity = await this.statusPageClient.getStatus();
        const lastDeployTimeMs =
            await this.statusPageClient.getMostRecentDeployTimeMs();
        if (
            severity === "none" &&
            lastDeployTimeMs > subHours(new Date().getTime(), 1).getTime() &&
            window.location.href.includes("teleop")
        ) {
            this.updateBannerStatus("deploying");
            return;
        }
        // Don't show the banner for minor outages
        if (
            severity === "critical" ||
            severity === "major" ||
            severity === "maintenance"
        ) {
            this.updateBannerStatus(severity);
        }
    }

    public render() {
        const { severity, message } = this;

        return (
            !!statuspageEndpoint && (
                <Accordion
                    className={classnames(
                        styles["outage-banner"],
                        styles[severity],
                    )}
                    visible={severity !== "none"}
                    maxHeight={30}
                >
                    <Icon className={styles.icon} name="warning" />
                    <div className={styles.message}>
                        {message}
                        <a
                            className={classnames(
                                styles.underlineText,
                                styles.padLeft,
                            )}
                            href={statuspageEndpoint}
                            target="_blank"
                            rel="noreferrer"
                        >
                            More information
                        </a>
                    </div>
                    <FlexSpacer />
                    <Icon
                        className={styles.closeButton}
                        name="close"
                        onClick={() => this.closeBanner()}
                    />
                </Accordion>
            )
        );
    }
}
